import {
  Flex,
  Box,
  Heading,
  Button,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import DescriptionGenerator from "components/addForm/autoGenerateDescription";
import ValidatedInput from "components/addForm/ValidatedInput";
import SelectInput from "components/addForm/SelectInput";
import FeaturesComponent from "views/admin/plans/FeaturesComponent";
import CalendarInput from "components/addForm/CalendarInput";
import { SEARCH_FOR } from "constants/defaultState";
import SearchInput from "components/addForm/searchInput";
import ValidatedInputWithCounter from "components/addForm/ValidatedInputWithCounter";

export const DISCOUNT_TYPE = {
  percentage: "percentage",
  amount: "amount",
};

const PLAN_TYPES = {
  pro: "PRO",
  pro_plus: "PRO_PLUS",
};

const PLAN_USER_TYPES = {
  tenant: "TENANT",
  buyer: "BUYER",
  landlord: "LANDLORD",
  seller: "SELLER",
  agent: "AGENT",
  pg_owner: "PG_OWNER",
  co_living: "CO_LIVING",
  pg_seeker: "PG_SEEKER",
};

const PLAN_CATEGORY = {
  user: "USER",
  ad: "AD",
};

const Plan = {
  name: (props) => {
    return (
      <ValidatedInput
        name="name"
        label="Name"
        placeholder="Enter Name"
        type="text"
        isRequired={true}
        {...props}
      />
    );
  },

  title: (props) => {
    return (
      <ValidatedInputWithCounter
        name="title"
        label="Title"
        placeholder="Enter Title"
        type="text"
        maxLength={65}
        {...props}
      />
    );
  },

  user_id: (props) => {
    const { user_id, is_custom } = props.formData;
    return (
      is_custom && (
        <SearchInput
          label="User Phone Number or ID"
          name="user_id"
          value={user_id}
          onChange={props.onChange}
          placeholder="Search by Phone Number"
          type="text"
          isRequired={true}
          error={props.error}
          setErrors={props.setErrors}
          searchFor={SEARCH_FOR.USER}
        />
      )
    );
  },

  description: (props) => (
    <DescriptionGenerator
      onChange={props.onChange}
      formData={props.formData}
      name="description"
      label="Description"
      placeholder="Enter Description"
      showButton={false}
      maxLength={300}
      value={props.formData?.description || ""}
      error={props.error}
    />
  ),

  type: (props) => (
    <SelectInput
      name="type"
      label="Type"
      options={PLAN_TYPES}
      selectedValue={props.value || ""}
      onChange={props.onChange}
      error={props.error}
    />
  ),

  user_type: (props) => (
    <SelectInput
      name="user_type"
      label="User Type"
      options={PLAN_USER_TYPES}
      selectedValue={props.value || ""}
      onChange={props.onChange}
      error={props.error}
    />
  ),

  category: (props) => (
    <SelectInput
      name="category"
      label="Category"
      options={PLAN_CATEGORY}
      selectedValue={props.value || ""}
      onChange={props.onChange}
      error={props.error}
    />
  ),

  features: (props) => <FeaturesComponent {...props} />,

  pricing: (props) => {
    const { formData = {}, onChange, error } = props;
    const pricing = formData.pricing || {
      description: "",
      duration_in_days: "",
      price: "",
      gst_included: false,
      discount: {},
      discount_validity: "",
    };

    const handlePricingChange = (e) => {
      const { name, value } = e.target;
      const updatedPricing = { ...pricing, [name]: value };
      onChange({
        target: {
          name: "pricing",
          value: updatedPricing,
        },
      });
    };

    return (
      <Box>
        <Heading as="h3" size="md" mb={4}>
          Pricing
        </Heading>
        <Flex direction={{ base: "column", md: "row" }} gap={4} mb={4}>
          <div style={{ flex: 1 }}>
            <ValidatedInput
              name="description"
              label="Description"
              placeholder="Enter Description"
              type="text"
              value={pricing?.description || ""}
              onChange={handlePricingChange}
              error={error?.description}
            />
          </div>
          <div style={{ flex: 1 }}>
            <ValidatedInput
              name="duration_in_days"
              label="Duration in Days"
              placeholder="Enter Duration in Days"
              type="number"
              value={pricing?.duration_in_days || ""}
              onChange={handlePricingChange}
              error={error?.duration_in_days}
            />
          </div>
          <div style={{ flex: 1, position: "relative" }}>
            <Box
              display="flex"
              alignItems="center"
              width={"fit-content"}
              position={"absolute"}
              right={0}
              zIndex={10}
            >
              <FormLabel mb="0">GST Included</FormLabel>
              <Switch
                name="gst_included"
                isChecked={pricing.gst_included}
                onChange={(e) =>
                  handlePricingChange({
                    target: {
                      name: "gst_included",
                      value: e.target.checked,
                    },
                  })
                }
                error={error?.price}
              />
            </Box>
            <ValidatedInput
              name="price"
              label="Price"
              placeholder="Enter Price"
              type="number"
              value={pricing?.price || ""}
              onChange={handlePricingChange}
              error={error?.price}
            />
          </div>
        </Flex>
        <Flex direction={{ base: "column", md: "row" }} gap={4}>
          <div style={{ flex: 1 }}>
            <SelectInput
              name="discount_type"
              label="Discount Type"
              options={DISCOUNT_TYPE}
              selectedValue={pricing?.discount?.type || ""}
              onChange={(e) => {
                const updatedPricing = {
                  ...pricing,
                  discount: {
                    ...pricing.discount,
                    type: e.target.value,
                    value: "",
                  },
                };
                onChange({
                  target: {
                    name: "pricing",
                    value: updatedPricing,
                  },
                });
              }}
              error={error?.discount?.type}
            />
          </div>
          <div style={{ flex: 1 }}>
            <ValidatedInput
              name="discount_value"
              label="Discount Value"
              placeholder={
                pricing?.discount?.type === DISCOUNT_TYPE.percentage
                  ? "Enter Discount Percentage"
                  : "Enter Discount Value"
              }
              type="number"
              isDisabled={!pricing?.discount?.type}
              value={pricing?.discount?.value || ""}
              onChange={(e) => {
                const value = e.target.value;
                const discountType = pricing?.discount?.type;
                const updatedPricing = {
                  ...pricing,
                  discount: {
                    ...pricing.discount,
                    value: Number(e.target.value),
                  },
                };
                if (discountType === DISCOUNT_TYPE.percentage && value <= 100) {
                  onChange({
                    target: {
                      name: "pricing",
                      value: updatedPricing,
                    },
                  });
                } else if (
                  discountType === DISCOUNT_TYPE.amount &&
                  value <= pricing?.price
                ) {
                  onChange({
                    target: {
                      name: "pricing",
                      value: updatedPricing,
                    },
                  });
                }
              }}
              error={error?.discount?.value}
              isRequired={pricing?.discount?.type}
            />
          </div>
          <div style={{ flex: 1 }}>
            <CalendarInput
              name="discount_validity"
              label="Discount Validity"
              value={pricing?.discount_validity || ""}
              onChange={(e) => {
                const updatedPricing = {
                  ...pricing,
                  discount_validity: e.target.value,
                };
                onChange({
                  target: {
                    name: "pricing",
                    value: updatedPricing,
                  },
                });
              }}
              error={error?.discount_validity}
              isRequired={pricing?.discount?.value && pricing?.discount?.type}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Button
              mt={8}
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={() => {
                const updatedPricing = {
                  ...pricing,
                  discount: undefined,
                  discount_validity: undefined,
                };
                onChange({
                  target: {
                    name: "pricing",
                    value: updatedPricing,
                  },
                });
              }}
            >
              Remove Discount
            </Button>
          </div>
        </Flex>
      </Box>
    );
  },
  valid_till: (props) => {
    const { is_custom } = props.formData;
    return (
      is_custom && (
        <Box mb={8}>
          <CalendarInput
            name="valid_till"
            label="Valid Till"
            value={props.value || ""}
            onChange={props.onChange}
            error={props.error}
            isRequired={is_custom}
          />
        </Box>
      )
    );
  },
};

export default Plan;
